import {
  getNextMatchingSibling,
  getPreviousMatchingSibling,
  slugify,
} from '../../utils'

// TODO: discuss markup changes to add ARIA attributes & update them with control state
// TODO: arrow key navigation through the filtered options, requires changes to focus states
const ENTER_KEYCODE = 13
const UP_KEYCODE = 38
const DOWN_KEYCODE = 40
const ESCAPE_KEYCODE = 27
const BACKSPACE_KEYCODE = 8
const SPACEBAR_KEY_CODE = [0, 32]

const isNavigation = (keycode) => [UP_KEYCODE, DOWN_KEYCODE].includes(keycode)
const isSpaceBar = (keycode) => SPACEBAR_KEY_CODE.includes(keycode)

const createEmptyOption = (optionsWrapperEl) => {
  const emptyOption = document.createElement('div')
  emptyOption.className = 'dropdown--option option--empty'
  const label = optionsWrapperEl.querySelector('.dropdown--label')
  if (label) {
    label.after(emptyOption)
  } else {
    optionsWrapperEl.prepend(emptyOption)
  }
}

// create one mutation observer for all dropdowns
// this captures any prop changes to the original input & updates the full control as expected

const observerConfig = {
  attributes: true,
  attributeFilter: ['disabled', 'readonly', 'required'],
}
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const { attributeName, target } = mutation
    if (attributeName === 'disabled') {
      // other input is disabled or enabled
      target.previousElementSibling.disabled = Boolean(target.disabled)
    }
    if (attributeName === 'readonly') {
      // note that MO checks 'readonly' but the prop is readOnly
      // toggles the extra class that controls dropdown styling/state
      target.classList.toggle('read-only', target.readOnly)
    }
    if (attributeName === 'required') {
      const isRequired = target.required
      // needs to hide, create, or show the empty option depending on required state
      const emptyEl =
        target.parentElement.nextElementSibling.querySelector('.option--empty')
      if (isRequired && emptyEl) {
        emptyEl.classList.add('option--hidden')
      } else {
        if (!emptyEl) {
          createEmptyOption(target.parentElement.nextElementSibling)
        } else {
          emptyEl.classList.remove('option--hidden')
        }
      }
    }
    // guards against prop changes reseting the visible value
    const { dropdownLabel = '' } = target.previousElementSibling.dataset
    target.value = dropdownLabel
    target.setAttribute('value', dropdownLabel)
  }
})

export const initializeFoundryDropDown = (el) => {
  const dropdownOptionWrapper = el.querySelector('.dropdown-menu__wrapper')
  const dropdownInputWrapper = el.querySelector('.dropdown-input__wrapper')
  const dropdownInput = dropdownInputWrapper.querySelector(
    '.input-fields__input'
  )
  const { id } = dropdownInput
  const { search } = dropdownInput.dataset
  const noTypeahead = search === 'false'
  let isReadOnly = Boolean(dropdownInput.readOnly) // store initial control state
  let isDisabled = Boolean(dropdownInput.disabled) // store initial control state

  // set up aria attributes
  const optionsId = dropdownOptionWrapper.id || `${id}-options`
  dropdownInput.setAttribute('aria-controls', optionsId)
  dropdownInput.setAttribute('aria-expanded', false)
  dropdownInput.setAttribute('role', noTypeahead ? 'listbox' : 'combobox')
  dropdownOptionWrapper.id = optionsId

  // "real" readOnly control, uses a class instead of native prop
  if (isReadOnly) {
    dropdownInput.classList.add('read-only')
  }

  // no typeahead search (more traditional dropdown rather than combobox style)
  if (noTypeahead) {
    dropdownInput.readOnly = true
  }

  // hidden input will contain the real value/props
  const dropdownValue = document.createElement('input')
  dropdownValue.type = 'hidden'
  dropdownValue.setAttribute('name', dropdownInput.getAttribute('name'))
  dropdownInput.removeAttribute('name')

  // disabled fields shouldn't send values on submit, so the hidden input must be disabled
  if (isDisabled) {
    dropdownValue.setAttribute('disabled', true)
  }

  // watch input for changes with MO to configure component
  observer.observe(dropdownInput, observerConfig)

  dropdownInput.insertAdjacentElement('beforebegin', dropdownValue)

  const { required } = dropdownInput
  if (!required) {
    createEmptyOption(dropdownOptionWrapper)
  }

  // Since options are an HTMLCollection they'll always reference
  // the current state of the DOM but we'll need to convert them
  // to an array whenever we want to to iterate over them
  const options = dropdownOptionWrapper.children

  const getLabel = (el) => el.innerText
  const getValue = (el) => el.getAttribute('data-dropdown-value')
  let selectedValue = ''
  let selectedLabel = ''
  let navigating = false
  let isToggleOpen = false

  const removeOptionsClasses = (...classes) =>
    Array.from(options).forEach((option) => option.classList.remove(...classes))

  const toggleDropdown = (attemptOpen) => {
    const anySelected = dropdownOptionWrapper.querySelector('.option--selected')
    const shouldOpen = attemptOpen
      ? !dropdownInput.disabled &&
        !dropdownInput.classList.contains('read-only')
      : false
    dropdownInput.setAttribute('aria-expanded', shouldOpen)
    dropdownInputWrapper.classList.toggle('dropdown-input__open', shouldOpen)
    isToggleOpen = shouldOpen
    if (!shouldOpen) {
      navigating = false
      removeOptionsClasses()
    }
    if (anySelected) {
      removeOptionsClasses('option--focused', 'option--selected')
      anySelected.classList.add('option--focused', 'option--selected')
      anySelected.scrollIntoView()
    }
  }

  const setValue = ({ value, label }, optionEl) => {
    selectedValue = value
    selectedLabel = label
    dropdownInput.setAttribute('value', label)
    dropdownInput.value = label
    dropdownValue.value = value
    dropdownValue.setAttribute('value', value)
    dropdownValue.setAttribute('data-dropdown-label', label)
    dropdownValue.dispatchEvent(
      new Event('input', { bubbles: true, cancelable: true })
    )
    removeOptionsClasses(
      'option--filtered',
      'option--selected',
      'option--focused'
    )
    navigating = false
    const previouslySelected =
      dropdownOptionWrapper.querySelector('[aria-selected]')
    if (previouslySelected) {
      optionEl.removeAttribute('aria-selected')
    }
    if (optionEl) {
      if (!optionEl.id) {
        optionEl.id = `op-${slugify(value)}-${slugify(label)}`
      }
      dropdownOptionWrapper.setAttribute('aria-activedescendant', optionEl.id)
      optionEl.setAttribute('aria-selected', true)
      optionEl.classList.add('option--selected', 'option--focused')
      optionEl.scrollIntoView()
    }
  }

  const clearValue = () => {
    selectedValue = ''
    selectedLabel = ''
    dropdownInput.value = ''
    dropdownValue.value = ''
    dropdownValue.setAttribute('data-dropdown-label', '')
    dropdownValue.dispatchEvent(
      new Event('input', { bubbles: true, cancelable: true })
    )
    removeOptionsClasses(
      'option--filtered',
      'option--selected',
      'option--focused'
    )
  }

  // handle initial default values
  const defaultValue = dropdownInput.getAttribute('value')
  if (defaultValue) {
    const selectedOption = dropdownOptionWrapper.querySelector(
      `[data-dropdown-value='${defaultValue}']`
    )
    const selectedOptionLabel = selectedOption
      ? selectedOption.innerText.trim()
      : ''
    if (selectedOptionLabel) {
      dropdownInput.removeAttribute('value')
      setValue(
        { value: defaultValue, label: selectedOptionLabel },
        selectedOption
      )
    } else {
      // value wasn't in the list, so can't be selected
      setValue({ value: '', label: '' })
    }
  }

  // Special Keys
  const focusAndScrollOption = (el) => {
    navigating = true
    removeOptionsClasses('option--focused')
    el.classList.add('option--focused')
    el.scrollIntoView()
  }

  dropdownInput.addEventListener('keydown', (event) => {
    const { which } = event
    if (
      isNavigation(which) &&
      dropdownOptionWrapper.querySelectorAll(':not(.option--filtered)').length
    ) {
      if (!isToggleOpen) {
        toggleDropdown(true)
      } else if (which === UP_KEYCODE) {
        const target = dropdownOptionWrapper.querySelector('.option--focused')
        removeOptionsClasses('option--focused')
        event.preventDefault()
        const lastUnfiltered = Array.from(
          dropdownOptionWrapper.querySelectorAll(':not(.option--filtered)')
        ).pop()
        if (target) {
          event.preventDefault()
          const previousOption = getPreviousMatchingSibling(
            target,
            ':not(.option--filtered)'
          )
          if (previousOption) {
            focusAndScrollOption(previousOption)
          } else {
            focusAndScrollOption(lastUnfiltered)
          }
        } else {
          focusAndScrollOption(lastUnfiltered)
        }
      } else if (which === DOWN_KEYCODE) {
        const target = dropdownOptionWrapper.querySelector('.option--focused')
        removeOptionsClasses('option--focused')
        event.preventDefault()
        const firstUnfiltered = dropdownOptionWrapper.querySelector(
          ':not(.option--filtered)'
        )
        if (target) {
          const nextOption = getNextMatchingSibling(
            target,
            ':not(.option--filtered)'
          )
          if (nextOption) {
            focusAndScrollOption(nextOption)
          } else {
            focusAndScrollOption(firstUnfiltered)
          }
        } else {
          focusAndScrollOption(firstUnfiltered)
        }
      }
    } else if (which === ESCAPE_KEYCODE) {
      toggleDropdown(false) // escape should close (and not select) controls
    } else if (navigating && (isSpaceBar(which) || which === ENTER_KEYCODE)) {
      const currentOption =
        dropdownOptionWrapper.querySelector('.option--focused')
      if (currentOption) {
        const value = getValue(currentOption)
        const label = getLabel(currentOption)
        toggleDropdown(false)
        setValue({ value, label }, currentOption)
        event.preventDefault()
      }
    } else if (which === ENTER_KEYCODE) {
      if (dropdownInput.value !== selectedValue) {
        toggleDropdown(true)
        if (selectedValue) {
          dropdownInput.value = selectedLabel
        } else {
          clearValue()
        }
      } else {
        if (dropdownInputWrapper.matches('.dropdown-input__open')) {
          toggleDropdown(false)
        } else {
          toggleDropdown(true)
        }
      }
    } else if (
      which === BACKSPACE_KEYCODE &&
      selectedValue &&
      dropdownInput.value === dropdownValue.getAttribute('data-dropdown-label')
    ) {
      clearValue() // backspace deletes entire selection when there's a selectedValue that matches current input
    } else {
      toggleDropdown(true) // otherwise, all other typing should just open the control
    }
  })

  // Input change processing
  dropdownInput.addEventListener('input', () => {
    // this could be better...
    // for fuzzy string matching/weighted match sorting, consider something like https://fusejs.io/
    if (dropdownInput.value.length >= 1) {
      const matcher = new RegExp(`${dropdownInput.value.toLowerCase()}`, 'i')
      Array.from(options).forEach((option) => {
        const label = getLabel(option)
        if (label && label.toLowerCase().match(matcher)) {
          option.classList.remove('option--filtered')
        } else {
          option.classList.add('option--filtered')
        }
      })
    } else {
      removeOptionsClasses('option--filtered')
    }
  })

  // all click events
  el.addEventListener('mousedown', (event) => {
    const { target } = event

    if (target.matches('.dropdown--option')) {
      const value = getValue(target)
      const label = getLabel(target)
      toggleDropdown(false)
      setValue({ value, label }, target)
      event.preventDefault()
      return false
    } else if (target === dropdownInput) {
      toggleDropdown(true)
    } else if (target.matches('.dropdown--indicator')) {
      const isOpen = dropdownInputWrapper.classList.contains(
        'dropdown-input__open'
      )
      event.preventDefault()
      if (isOpen) {
        dropdownInput.blur()
      } else if (
        !dropdownInput.disabled &&
        !dropdownInput.classList.contains('read-only')
      ) {
        dropdownInput.focus()
        toggleDropdown(true)
      }
    } else if (!el.contains(target)) {
      // clicking anywhere outside the control closes it
      toggleDropdown(false)
    }
  })

  // keyboard focus and ensuring current option is visible
  dropdownInput.addEventListener('focus', () => {
    toggleDropdown(true)
    const selectedEl = dropdownInputWrapper.querySelector('.option--selected')
    if (selectedEl) {
      selectedEl.scrollIntoView()
    }
  })

  // blur, and preventing the loss of focus on select
  dropdownInput.addEventListener('blur', (event) => {
    const { target } = event
    if (target === dropdownInput) {
      if (selectedValue) {
        dropdownInput.value = selectedLabel // revert to last good value on blur w/ good value
      } else {
        clearValue()
      }
    }
    if (!dropdownInput.activeInput && !target.matches('.dropdown--option')) {
      toggleDropdown(false)
    } else if (target.matches('.dropdown--option')) {
      event.preventDefault() // prevent control blur
    }
  })
}
